import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiRequest } from '../api/client.js';

/**
 * Global status in the shell (redesign Phase B).
 *
 * ==================== AN INSTRUMENT, NOT A BANNER ====================
 * The Dashboard shows an attention alert ONLY when something is wrong, and its
 * comment explains why: "a permanent 'all good' banner is scrolled past within
 * a week". That is right for a banner and wrong for this.
 *
 * Connaro runs beside a live broadcast. The question "are my platforms
 * delivering right now" is one an operator asks constantly and can currently
 * only answer by navigating to Connections — so the shell reads out a number
 * the way a meter does, permanently and quietly, and raises its voice only
 * when something needs doing.
 *
 * The DEFINITION of "needs attention" is the server's, and the same one the
 * Dashboard uses (`requiresCustomerAction` plus DISCONNECTED). A second
 * definition here is how a header and a dashboard end up disagreeing about
 * whether a broadcast is healthy.
 * =====================================================================
 */

type Status = {
  live: number;
  connected: number;
  attention: { platform: string; label: string }[];
};

/**
 * How often to re-read.
 *
 * Thirty seconds, not a socket. Connection status changes are rare and the
 * cost of being half a minute late is nil — whereas a socket in the shell
 * would be a second realtime consumer to reason about on every page, for a
 * number that changes a few times a day.
 */
const POLL_MS = 30_000;

export function WorkspaceStatus(): React.JSX.Element | null {
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let cancelled = false;

    const read = async (): Promise<void> => {
      try {
        const next = await apiRequest<Status>('/workspace-status');
        if (!cancelled) setStatus(next);
      } catch {
        /*
         * Silent. This is an ornament on every page in the product, and a
         * workspace with no active membership, a transient 500 or a dropped
         * network would otherwise put an error in the header of a page that
         * is working perfectly.
         */
      }
    };

    void read();
    const timer = setInterval(() => void read(), POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // Nothing is claimed until the server has answered, and a workspace with no
  // connections gets no readout rather than a confident "0 live".
  if (status === null || status.connected === 0) return null;

  const needsAttention = status.attention.length > 0;

  return (
    <div className="wstatus" role="status">
      {/*
        * A dot AND a word. §65: meaning never rests on colour alone, and this
        * is read at a glance by somebody mid-broadcast.
        */}
      <span className={`wstatus__dot wstatus__dot--${needsAttention ? 'warn' : 'ok'}`} aria-hidden="true" />
      <span className="wstatus__live">
        {status.live} of {status.connected} live
      </span>

      {needsAttention && (
        <Link className="wstatus__attention" to="/connections">
          {status.attention.length} need
          {status.attention.length === 1 ? 's' : ''} attention
        </Link>
      )}
    </div>
  );
}
